import { FirmDataHolding } from '@/api/custom';
import { HoldingPosition } from '@/util/planning-util';

/**
 * Map firm-data holdings onto the Apex Position shape the wealth page's
 * Holdings / Allocation tabs already render.
 *
 * Those tabs read `symbol`, `description`, `amount`, `stock_unit_price`, and
 * `value_usd`, so firm-data rows are mapped onto those same keys instead of
 * teaching the UI a second shape.
 */

const ASSET_CLASS_TO_APEX_TYPE: Record<string, HoldingPosition['asset_type']> = {
  equity: 'EQUITY',
  stock: 'EQUITY',
  option: 'OPTION',
  currency: 'CURRENCY',
  cash: 'CURRENCY',
  digital: 'DIGITAL',
  crypto: 'DIGITAL',
  fixed_income: 'FIXED_INCOME',
  bond: 'FIXED_INCOME',
  mutual_fund: 'MUTUAL_FUND',
  fdic_synthetic: 'FDIC_SYNTHETIC',
};

const assetType = (assetClass?: string | null): HoldingPosition['asset_type'] => {
  const key = (assetClass || '').trim().toLowerCase().replace(/ /g, '_');
  return ASSET_CLASS_TO_APEX_TYPE[key] ?? 'TYPE_UNSPECIFIED';
};

const asNumber = (value?: string | null): number => {
  if (!value) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const toApexHoldings = (holdings: FirmDataHolding[]): HoldingPosition[] =>
  holdings.map((holding) => {
    const quantity = asNumber(holding.quantity);
    const marketValue = asNumber(holding.market_value);
    const symbol = (holding.symbol || '').trim();
    const name = (holding.name || '').trim() || symbol;
    const unitPrice = quantity !== 0 ? String(marketValue / quantity) : null;
    const apexAssetType = assetType(holding.asset_class);
    // `amount` is shares for every Apex type except CURRENCY, where
    // makeHoldingsNumerical reads it as dollars and skips the unit-price
    // multiply. firm-data keeps shares in `quantity` for cash rows too (a
    // sweep fund's shares are not its dollars), so send market value there
    // or allocation would count a $100 cash position as its share count.
    const amount = apexAssetType === 'CURRENCY' ? marketValue : quantity;
    return {
      cost_basis_metadata: holding.cost_basis_metadata,
      asset_id: holding.instrument_id || '',
      amount: String(amount),
      symbol,
      cusip: '',
      description: name,
      asset_type: apexAssetType,
      stock_unit_price: unitPrice,
      stock_unit_price_date: holding.as_of_date ?? null,
      value_usd: marketValue,
    } as HoldingPosition;
  });

export default toApexHoldings;
